import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';

export interface UiMessages {
  badge: { remove: string };
  breadcrumbs: { root: string };
  calendar: {
    previousMonth: string;
    nextMonth: string;
    months: string[];
    weekdaysShort: string[];
    weekdaysFull: string[];
  };
  carousel: {
    root: string;
    previous: string;
    next: string;
    slide: (index: number, total: number) => string;
    goToSlide: (index: number) => string;
  };
  closeButton: { close: string };
  currencyInput: { currency: string; names: Record<string, string> };
  datePicker: {
    today: string;
    cancel: string;
    apply: string;
    startPlaceholder: string;
    endPlaceholder: string;
  };
  filter: {
    open: string;
    sheetTitle: string;
    close: string;
    add: string;
    clear: string;
    apply: string;
    remove: string;
    emptyTitle: string;
    emptyText: string;
    field: string;
    selectField: string;
    operator: string;
    value: string;
    selectValue: string;
    enterValue: string;
    valueFrom: string;
    valueTo: string;
    rangeFrom: string;
    rangeTo: string;
    clearDate: string;
    selected: (count: number) => string;
    operatorEquals: string;
    operatorContains: string;
    operatorBetween: string;
    operatorOnOrBefore: string;
    operatorGreaterThan: string;
    operatorLessThan: string;
  };
  mobileNavDrawer: { open: string; close: string };
  modal: { close: string; dontShowAgain: string; settings: string };
  multiSelect: {
    placeholder: string;
    noResults: string;
    reset: string;
    selectAll: string;
    remove: (label: string) => string;
  };
  notification: { close: string };
  notificationProvider: { region: string };
  otpInput: { digit: string };
  pagination: {
    root: string;
    previous: string;
    next: string;
    pageInfo: (page: number, totalPages: number) => string;
    goToPage: (page: number) => string;
  };
  paginationDots: { slides: string; goToSlide: (index: number) => string };
  phoneInput: { country: string };
  progress: { root: string };
  select: {
    placeholder: string;
    noResults: string;
    /** The creatable mode's last row (HS-1633). */
    create?: (value: string) => string;
  };
  slider: { minimum: string; maximum: string; value: string };
  table: {
    selectAll: string;
    selectRow: string;
    actions?: string;
    search: string;
    loading: string;
    sortBy: (header: string) => string;
  };
  tag: { remove: string };
  tagInput: {
    suggestions: string;
    create: (value: string) => string;
    remove: (value: string) => string;
  };
  timePicker: {
    hour: string;
    minute: string;
    cycle24: string;
    cycle12: string;
    cancel: string;
    apply: string;
    hoursColumn: string;
    minutesColumn: string;
    periodColumn: string;
    am: string;
    pm: string;
    cycleGroup: string;
  };
  versionOverlay: { copy: string; copied: string };
  videoPlayer: {
    root: string;
    play: string;
    pause: string;
    mute: string;
    unmute: string;
    seek: string;
    /** The read-only indicator shown in place of the scrubber when seeking is disabled. */
    progress: string;
    volume: string;
    speed: string;
    enterFullscreen: string;
    exitFullscreen: string;
  };
}

export type UiMessagesOverrides = {
  [Section in keyof UiMessages]?: Partial<UiMessages[Section]>;
};

export interface UiMessagesProviderProps {
  children: ReactNode;
  /**
   * Already-translated UI messages. A nested provider inherits unspecified sections and values from
   * its parent, so a screen can override one label without rebuilding the app-level dictionary.
   */
  value: UiMessagesOverrides;
}

const UiMessagesContext = createContext<UiMessagesOverrides>({});

export function UiMessagesProvider({ children, value }: UiMessagesProviderProps) {
  const parent = useContext(UiMessagesContext);
  const merged = useMemo<UiMessagesOverrides>(() => {
    const result: UiMessagesOverrides = { ...parent };

    for (const section of Object.keys(value) as (keyof UiMessages)[]) {
      result[section] = { ...parent[section], ...value[section] } as never;
    }

    return result;
  }, [parent, value]);

  return <UiMessagesContext.Provider value={merged}>{children}</UiMessagesContext.Provider>;
}

/** Read the nearest translated UI messages. Component props should override these values. */
export function useUiMessages(): UiMessagesOverrides {
  return useContext(UiMessagesContext);
}
